// # css
import "./dashboard.css";

// # lib
import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  defaults,
} from "chart.js";
import { Line } from "react-chartjs-2";
// # assets
import addColumnClose from "../../assets/close.svg";
import tableArrowAscending from "../../assets/dashboard-table-arrow-asc.svg";
import tableArrowDescending from "../../assets/dashboard-table-arrow-desc.svg";

import tableDeleteColumn from "../../assets/dashboard-table-delete-column.svg";
// # components
import AnimationPage from "../shared/animation-page/AnimationPage";
import GTable from "../design/components/GTable";
import Button from "../design/Button";
import { Filters } from "./Filters";
import { JService } from "../api-service/ApiService";
import { enqueueSnackbar } from "notistack";
import {
  formatTime,
  getHMSfromMins,
  formatDate as calcDate,
  filtersReducer,
  getOrgURLParam,
  getPermissionId,
  capitalizeString,
} from "../../utility-functions";
import { MoreVert } from "@mui/icons-material";
import Pusher from "pusher-js";
import { saveAccountDetails } from "../features/account/account";
import { reduxPersistSelectedVirtualNumber } from "../features/virtual-number/virtualNumber";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const options = {
  responsive: true,

  scales: {
    x: {
      grid: {
        display: true,
        drawOnChartArea: false,
        drawTicks: true,
        tickLength: 8,
        color: "rgba(0, 0, 0, 0.3)",
        drawBorder: false,
      },
      ticks: {
        display: true,
      },
    },
    y: {
      grid: {
        display: true,
        color: "rgba(0, 0, 0, 0.3)",
        borderDash: [8, 4],
        drawTicks: true,
        tickLength: 8,
        drawBorder: false,
      },
    },
  },
  layout: {
    padding: {
      left: 32,
      // right:0,
      // bottom:0,
      right: 32,
      top: 0,
      bottom: 0,
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    title: {
      display: true,
      text: "Call volume",
      font: {
        size: 16,
        weight: "400",
      },
      padding: 32,
      align: "start",
    },
  },
};

const PUSHER_KEY = process.env.REACT_APP_PUSHER_KEY;
const PUSHER_CLUSTER = process.env.REACT_APP_PUSHER_CLUSTER;

const columnTypeMapping = {
  Status: "string",
  "Last activity": "date",
  User: "object",
};
const sortableKeys = { User: "name" };

const getOrdinalDate = (dateString) => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "short" });

  return `${day} ${month}`;
};

const getFormattedTime = (minutes) => {
  const { hrs, mins, secs } = getHMSfromMins(minutes);
  if (hrs === 0 && mins === 0 && secs === 0) {
    return 0;
  }

  let formattedTime = "";

  if (hrs > 0) {
    formattedTime += `${hrs}h `;
  }
  if (hrs > 0 || mins > 0) {
    formattedTime += `${mins}m `;
  }
  if (hrs > 0 || mins > 0 || secs > 0) {
    formattedTime += `${secs}s`;
  }

  return formattedTime.trim();
};

export default function NewDashboard() {
  const dispatch = useDispatch();
  const pusherRef = useRef({ pusher: null, channelName: null });
  const calendarRef = useRef(null);

  const reduxOrg = useSelector((state) => state?.account?.value);
  const reduxSubscription = useSelector(
    (state) => state?.account?.value?.subscription,
  );
  const { canViewUsers, canViewTeams, canViewContactsAndLists } = useSelector(
    (state) => state?.auth?.permissions,
  );

  const isPro = reduxSubscription?.plan?.name?.includes("Professional");

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [gtableCols, setGtableCols] = useState([]);
  const [addColumnModalVisible, setAddColumnModalVisible] = useState(false);
  const [optionsPosition, setOptionsPosition] = useState("");
  const [columnClicked, setColumnClicked] = useState({
    field: "",
    headerName: "",
    type: "string",
  });
  const [rows, setRows] = useState([]);
  const [rowsToDisplay, setRowsToDisplay] = useState([]);
  const [chartData, setChartData] = useState({
    datasets: [],
  });
  const [csvData, setCSVData] = useState({
    headers: [],
    data: [],
    ids: [],
  });
  const [columns, setColumns] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [controller, setController] = useState(0);
  const [resizeController, setResizeController] = useState(0);
  const [errorBorder, setErrorBorder] = useState({
    colNameError: false,
  });
  const [addColumn, setAddColumn] = useState({
    name: "",
    data: "",
    call_type: "",
    call_status: "",
    contact_status: "",
    duration_filter: { filter: "none", start: "", end: "" },
  });
  const [analyticsData, setAnalyticsData] = useState({
    total_calls: 0,
    total_duration: 0,
    answered_calls: 0,
    unanswered_calls: 0,
    total_users: 0,
    unique_calls: 0,
    split: [],
  });
  const [gtableData, setGtableData] = useState([]);
  const [optionsVisible, setOptionsVisible] = useState(false);
  const formatDate = (date) => {
    return `${new Date(date).getFullYear()}-${(
      "0" +
      (new Date(date).getMonth() + 1)
    ).slice(-2)}-${("0" + new Date(date).getDate()).slice(-2)}`;
  };

  const [calendar, setCalendar] = useState({
    date_start: formatDate(new Date()),
    date_end: formatDate(new Date()),
    time_start: "00:00:00",
    time_end: "23:59:59",
  });

  const initialState = {
    users: { all: [], selected: [] },
    teams: { all: [], selected: [], teamUserMapping: {} },
    lists: { all: [], selected: [] },
    searchText: "",
    searchFields: ["User"],
  };

  const [availableData, dispatchAvailableData] = useReducer(
    filtersReducer,
    initialState,
  );

  const selectedUserIds = useMemo(() => {
    const isUserFilter = availableData.users.selected.length > 0;
    const isTeamFilter = availableData.teams.selected.length > 0;

    if (!isUserFilter && !isTeamFilter) return null;

    const targetUserIds = new Set();
    availableData.users.selected.forEach((u) => targetUserIds.add(String(u)));

    const teamUserMapping = availableData.teams.teamUserMapping;

    // Collect IDs from selected teams
    availableData.teams.selected.forEach((teamId) => {
      const memberIds = teamUserMapping[teamId] || [];
      memberIds.forEach((id) => targetUserIds.add(String(id)));
    });

    return Array.from(targetUserIds).join(",");
  }, [
    availableData.users.selected,
    availableData.teams.selected,
    availableData.teams.teamUserMapping,
  ]);

  const contactListIds = useMemo(() => {
    return availableData.lists.selected.join(",");
  }, [availableData.lists.selected]);

  const teamIds = useMemo(() => {
    return availableData.teams.selected.join(",");
  }, [availableData.teams.selected]);

  defaults.font.family = "Inter";
  useEffect(() => {
    setCSVData((prevState) => {
      let newIds = [];
      let newData = [];
      const activityIdx = [];
      const newHeaders = gtableCols?.map((col, idx) => {
        if (col.type === "dateTime") activityIdx.push(idx);
        return [col.headerName];
      });
      newHeaders.splice(1, 0, ["Email"]);

      for (let i = 0; i < rowsToDisplay.length; i++) {
        let item = rowsToDisplay[i];
        if (!newIds?.includes(item?.id)) {
          newIds.push(item?.id);
          let temp = gtableCols?.map((col, idx) => {
            const val = item[col.field] ?? item[col.headerName];
            if (val && typeof val === "object")
              return Object.values(val)?.[0];
            else if (val && activityIdx.includes(idx)) {
              return `${calcDate(val)} ${formatTime(val)}`;
            } else if (
              (val || val === 0) &&
              (col["colData"] === "total minutes" ||
                col["colData"] === "average minutes")
            ) {
              const formattedTime = getFormattedTime(val);
              return formattedTime === 0 ? "0" : formattedTime;
            } else return val === 0 || val ? String(val) : "0";
          });
          temp.splice(1, 0, item?.User?.email);
          newData.push(temp);
        }
      }
      return { ...prevState, headers: newHeaders, data: newData, ids: newIds };
    });
  }, [rowsToDisplay]);

  useEffect(() => {
    // simple search
    const searchTextLower = availableData?.searchText?.toLowerCase();
    let finalResult = [];
    let intermediateResult = []; // eslint-disable-line
    availableData?.searchFields?.forEach((searchField) => {
      let intermediateResult = rows?.filter((row) => {
        if (typeof row[searchField] === "object")
          return Object.values(row[searchField])?.some((val) =>
            val.toLowerCase()?.includes(searchTextLower),
          );
        else return row[searchField]?.toLowerCase()?.includes(searchTextLower);
      });
      finalResult = finalResult.concat(intermediateResult);
    });

    setRowsToDisplay(finalResult);
  }, [availableData?.searchText, rows]); //eslint-disable-line

  //   window.onDOMContentLoaded = handleChartsSize();

  // // init & resize & retrieve users & storeCampaigns(from /retrieve-contacts-lists/)

  function handleWindowResize() {
    setResizeController((prevState) => ++prevState);
  }

  useEffect(() => {
    handleChartsSize();
  }, [resizeController]);

  function handleChartsSize() {
    // const __statsContainer = document.querySelector(
    //   ".dashboard-overview-stats-container"
    // );
    // if (__statsContainer)
    //   __statsContainer.style.width = `calc(${document.body.clientWidth}px - var(--side-nav-width) - 64px)`;
    //FIX://resize the plotly line chart
  }

  useEffect(() => {
    window.addEventListener("resize", handleWindowResize);

    handleChartsSize();
    return () => {
      window.removeEventListener("resize", handleChartsSize);
    };
  }, []);

  // retrieve-call-analytics response cleaned
  const generateDataFromResponse = (responseData, userLevelAnalytics = []) => {

    const allUsers = availableData?.users?.all || [];

    const returnData = (userLevelAnalytics || [])?.map((analytics) => {
      const email = analytics.creator_email;
      const u = allUsers.find((au) => au.email?.toLowerCase() === email?.toLowerCase()) || {};
      
      let row = {
        creator: email,
        creatorName:
          (
            analytics.creator_name ||
            u.name ||
            [u.first_name, u.last_name].filter(Boolean).join(" ")
            )?.trim() || email,
        calls: u.calls || [],
        creatorId: u.id || email,
        status: u.status || "--",
        last_activity: u.last_activity || "--",
        assigned_contacts: u.assigned_contacts || [],
      };

      if (Array.isArray(analytics.data)) {
        analytics.data.forEach((item) => {
          let value = item.column_value;
          if (
            (item.column_type === "total minutes" ||
              item.column_type === "average minutes") &&
            typeof value === "number"
          ) {
            value = value / 60000;
          }

          row[item.column_name] = value;

          if (item.column_type === "status") {
            row["Status"] = value;
            row.status = value;
          }

          if (item.column_type === "last activity") {
            row.last_activity = value;
          }
        });
      }

      return row;
    });

    return returnData;
  };

  useEffect(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return;
    getFilteredData(data);
  }, [availableData.users, availableData.teams, availableData.lists, data]);

  const getFilteredData = (data) => {
    if (
      availableData?.users?.selected?.length === 0 &&
      availableData?.teams?.selected?.length === 0
    ) {
      setFilteredData(data);
      setShowLoader(false);
      return;
    }
    let temp = [];
    const usersIds = new Set();
    if (availableData?.users?.selected?.length >= 1) {
      availableData?.users?.selected?.forEach((u) => usersIds.add(String(u)));
    }

    const selectedTeams = availableData?.teams?.selected;

    if (selectedTeams.length >= 1) {
      selectedTeams.forEach((tid) => {
        availableData.teams.teamUserMapping[`${tid}`]?.forEach((u) =>
          usersIds.add(String(u)),
        );
      });
    }

    temp = data.filter((user) => {
      if (usersIds.has(String(user.creatorId))) return true;
    });

    setFilteredData(temp);
    setShowLoader(false);
    return temp;
  };

  const setDataForChart = (splitData) => {
    if (!splitData || !Array.isArray(splitData)) return;

    setChartData({
      labels: splitData.map((item) => {
        if (item.interval?.includes(":")) {
          return item.interval;
        }
        return getOrdinalDate(item.interval);
      }),
      datasets: [
        {
          label: "Total calls",
          data: splitData.map((item) => Number(item.total_calls)),
          borderColor: "rgb(129, 139, 240)",
          backgroundColor: "rgba(129, 139, 240)",
        },
        {
          label: "Answered calls",
          data: splitData.map((item) => Number(item.answered_calls)),
          borderColor: "rgb(62, 207, 142)",
          backgroundColor: "rgba(62, 207, 142)",
        },
        {
          label: "Unanswered calls",
          data: splitData.map((item) => Number(item.unanswered_calls)),
          borderColor: "rgb(219,120,221)",
          backgroundColor: "rgba(219,120,221)",
        },
      ],
    });
  };

  useEffect(() => {
    const abortController = new AbortController();
    fetchCallAnalytics(calendar, abortController.signal);

    return () => abortController.abort();
  }, [controller, selectedUserIds, contactListIds, teamIds, calendar]);

  const fetchCallAnalytics = (calendar, signal, skipChart = false) => {
    setData("");
    setGtableData("");
    if (!skipChart) {
      setAnalyticsData({
        total_calls: 0,
        total_duration: 0,
        answered_calls: 0,
        unanswered_calls: 0,
        total_users: 0,
        unique_calls: 0,
        split: [],
      });
      setChartData({
        datasets: [],
      });
    }

    if (
      selectedUserIds === "" &&
      contactListIds === "" &&
      teamIds === ""
    ) {
      setAnalyticsData({
        total_calls: 0,
        total_duration: 0,
        answered_calls: 0,
        unanswered_calls: 0,
        total_users: 0,
        unique_calls: 0,
        split: [],
      });
      setChartData({
        datasets: [],
      });
      setData([]);
      setGtableData([]);
      setFilteredData([]);
      setRows([]);
      setRowsToDisplay([]);
      setShowLoader(false);
      return;
    }

    setShowLoader(true);

    let orgUrlParam = getOrgURLParam();
    let url = `/api/v1/analytics/retrieve-call-analytics/${orgUrlParam}?date_start=${calendar?.date_start}&date_end=${calendar?.date_end}&time_start=${calendar.time_start}&time_end=${calendar.time_end}`;
    if (selectedUserIds) {
      url += `&selected_user_ids=${selectedUserIds}`;
    }
    if (contactListIds) {
      url += `&contact_list_ids=${contactListIds}`;
    }
    if (teamIds) {
      url += `&team_ids=${teamIds}`;
    }
    JService.get(url, {}, { signal })
      .then((res) => {
        if (!("success" in res)) return;
        if (!res?.success) {
          throw new Error(
            res?.message ||
              res?.detail ||
              "Error while fetching data for dashboard",
          );
        }
        if (
          !res?.user_level_analytics ||
          res?.user_level_analytics?.length === 0
        ) {
          enqueueSnackbar(
            (res?.message || "No data found") +
              ". No calls were made in the selected time range",
            { variant: "error" },
          );

          setData([]);
          setGtableData([]);
          setFilteredData([]);
          setRows([]);
          setRowsToDisplay([]);
          setShowLoader(false);
          return;
        }

        // subscribe to pusher
        const channelName = orgUrlParam.length
          ? orgUrlParam.replace("/", "")
          : String(reduxOrg?.data?.profile?.org);
        if (isPro) {
          connectPusher(channelName);
        }
        if (res?.analytics && !skipChart) {
          setAnalyticsData(res.analytics);
          setDataForChart(res.analytics.split);
        }
        let temp = generateDataFromResponse(null, res?.user_level_analytics);
        setData(temp);
        handleChartsSize();
        setShowLoader(false);
      })
      .catch((err) => {
        console.log(err);
        setData([]);
        setGtableData([]);
        setFilteredData([]);
        setRows([]);
        setRowsToDisplay([]);
        setShowLoader(false);
        handleChartsSize();
        enqueueSnackbar(err?.message || "Error occurred while fetching data", {
          variant: "error",
        });
      });
  };

  useEffect(() => {
    return () => disconnectPusher();
  }, []);

  useEffect(() => {
    calendarRef.current = calendar;
  }, [calendar]);

  const disconnectPusher = () => {
    if (pusherRef.current?.pusher) {
      console.log("disconnecting pusher");
      pusherRef.current.pusher.unsubscribe(pusherRef.current.channelName);
      pusherRef.current.pusher = null;
      pusherRef.current.channelName = null;
    }
  };
  const connectPusher = (channelName) => {
    if (pusherRef.current.pusher) {
      return;
    }
    pusherRef.current.pusher = new Pusher(PUSHER_KEY, {
      cluster: PUSHER_CLUSTER,
      encrypted: true,
    });
    pusherRef.current.channelName = channelName;

    const channel = pusherRef.current.pusher.subscribe(channelName);
    channel.bind("user_status", (pusherUpdate) => {
      handlePusherUpdate(pusherUpdate);
    });
  };

  function handlePusherUpdate(pusherUpdate) {
    const newStatus = pusherUpdate?.status;
    if (
      !newStatus ||
      (pusherUpdate?.user_id === undefined && !pusherUpdate?.user_email)
    ) {
      return;
    }

    const matchesRow = (row) => {
      if (
        pusherUpdate?.user_email &&
        (row?.creator?.toLowerCase() === pusherUpdate.user_email.toLowerCase() ||
          String(row?.creatorId)?.toLowerCase() ===
            pusherUpdate.user_email.toLowerCase())
      ) {
        return true;
      }
      return (
        pusherUpdate?.user_id !== undefined &&
        String(row?.creatorId) === String(pusherUpdate?.user_id)
      );
    };

    const normalizedStatus = newStatus.toLowerCase();
    const isOnCall = normalizedStatus === "on a call";
    const isAvailable = normalizedStatus === "available";

    const shouldAppendCall =
      isAvailable &&
      new Date(pusherUpdate.call_data?.end) <=
        new Date(`${calendarRef.current?.date_end}:23:59:59`) &&
      new Date(pusherUpdate.call_data?.end) >=
        new Date(`${calendarRef?.current?.date_start}:00:00:00`);

    setData((rows) => {
      if (!rows || !Array.isArray(rows)) return rows;
      return rows.map((row) => {
        if (!matchesRow(row)) return row;

        const updated = { ...row, status: newStatus, Status: newStatus };

        if (isOnCall) {
          updated.last_activity = "--";
        } else if (isAvailable) {
          updated.last_activity = pusherUpdate.call_data?.end ?? row.last_activity;
          updated.calls = shouldAppendCall
            ? [...(row.calls || []), pusherUpdate.call_data]
            : [...(row.calls || [])];
        }

        return updated;
      });
    });
  }

  //To initially fetch columns metadata,users and teams
  useEffect(() => {
    const handleSetUsers = (data) => {
      dispatchAvailableData({
        type: "setPayloadToAllOfType",
        payload: {
          payloadFor: "users",
          payload: data?.map((u) => ({
            name: (u?.first_name + " " + u?.last_name)?.trim() || u?.email,
            id: u?.id,
            email: u?.email,
          })),
        },
      });
    };
    const handleSetLists = (data) => {
      dispatchAvailableData({
        type: "setPayloadToAllOfType",
        payload: {
          payloadFor: "lists",
          payload: data?.map((l) => ({
            name: l?.title,
            id: l?.id,
          })),
        },
      });
    };
    const handleSetTeams = (data) => {
      const teamUserMapping = {};
      data?.forEach((u) => {
        teamUserMapping[u.id] =
          u.users?.map((user) => (typeof user === "object" ? user.id : user)) ||
          [];
      });
      dispatchAvailableData({
        type: "setPayloadToAllOfType",
        payload: {
          payloadFor: "teams",
          payload: data?.map((u) => ({
            name: u.name,
            id: u.id,
            // users: u.users || [],
          })),
          extraPayload: teamUserMapping,
        },
      });
    };

    if (reduxSubscription?.plan?.name?.includes("Standard"))
      setColumns(defaultColData);
    else fetchColumns();

    if (canViewUsers) {
      fetchData(
        `/api/v1/auth/retrieve-users/${getOrgURLParam()}?permission_id=${getPermissionId(null, "dashboard")}`,
        handleSetUsers,
        "users",
      );
    }
    if (canViewTeams) {
      fetchData(
        `/api/v1/auth/teams/?permission_id=${getPermissionId(null, "dashboard")}`,
        handleSetTeams,
        "teams",
      );
    }
    if (canViewContactsAndLists) {
      fetchData("/api/v1/core/contact-lists/", handleSetLists, "contact lists");
    }
  }, [canViewUsers, canViewTeams, canViewContactsAndLists]);

  const fetchData = (url, setterFn, resourceName) => {
    JService.get(url)
      .then((res) => {
        if (!res?.success)
          throw new Error(res?.message || `Could not retrieve ${resourceName}`);
        setterFn(res?.data);
      })
      .catch((err) => console.warn(`Could not retrieve ${resourceName}`, err));
  };

  const fetchColumns = () => {
    JService.get("/api/v1/auth/retrieve-account-profile/")
      .then((res) => {
        if (!res?.success) {
          throw new Error(
            res?.message || "Failed to retrieve account details.",
          );
        }
        dispatch(saveAccountDetails(res));
        if (res?.data?.profile?.primary_virtual_number)
          dispatch(
            reduxPersistSelectedVirtualNumber(
              res?.data?.profile?.primary_virtual_number,
            ),
          );

        const metrics = res?.data?.profile?.analytics_metrics;
        const hasMetrics = Array.isArray(metrics) && metrics.length > 0;

        setColumns(hasMetrics ? metrics : defaultColData);
      })
      .catch((error) => {
        console.warn("Error fetching account profile columns:", error);
      });
  };

  useEffect(() => {
    if (!columns || !Array.isArray(columns) || columns.length === 0) return;
    if (!data || !Array.isArray(data) || data.length === 0) return;

    createGtableColMetadata(columns);

    getCustomColumnData(columns);
  }, [columns, data, gtableData, filteredData]);

  const getCustomColumnData = (columns) => {
    if (!data || !Array.isArray(data) || data.length === 0) return;
    const newdata = filteredData.map((u) => {
      const obj = {
        id: u.creatorId,
        User: { name: u.creatorName, email: u.creator },
      };
      return obj;
    });

    columns.forEach((column) => {
      const {
        data: column_data,
        name: column_name,
        field: column_field,
      } = column;

      const targetField = column_field || column_name;

      if (
        ["id", "User"].includes(column_name) ||
        ["id", "User"].includes(targetField)
      )
        return;

      if (column_data === "status") {
        filteredData.forEach((u, i) => {
          newdata[i][targetField] = u.status;
          newdata[i][column_name] = u.status;
          newdata[i]["Status"] = u.status;
        });
        return;
      }
      if (column_data === "last activity") {
        filteredData.forEach((u, i) => {
          newdata[i][targetField] = u.last_activity;
          newdata[i][column_name] = u.last_activity;
        });
        return;
      }

      const findValue = (user) => {
        if (user[column_name] !== undefined) return user[column_name];
        if (user[targetField] !== undefined) return user[targetField];
        const lowerName = column_name?.toLowerCase();
        const lowerField = targetField?.toLowerCase();
        const key = Object.keys(user).find(
          (k) =>
            k.toLowerCase() === lowerName || k.toLowerCase() === lowerField,
        );
        if (key) return user[key];
        return undefined;
      };

      if (filteredData.some((u) => findValue(u) !== undefined)) {
        filteredData.forEach((u, i) => {
          const val = findValue(u);
          newdata[i][targetField] = val;
          if (column_name !== targetField) {
            newdata[i][column_name] = val;
          }
          if (column_data === "status") {
            newdata[i]["Status"] = val;
          }
        });
        return;
      }
    });

    setRowsToDisplay(newdata);
    setRows(newdata);
  };

  const clickCSVDownloadIcon = () => {
    console.log("data that will be downloaded: ", rowsToDisplay);
  };

  const sortRows = (rowsdata, order, fn = null) => {
    if (order === "sort-asc")
      rowsdata.sort((a, b) => {
        const aval = fn ? fn(a) : a[columnClicked.headerName];
        const bval = fn ? fn(b) : b[columnClicked.headerName];
        return aval > bval ? 1 : aval === bval ? 0 : -1;
      });
    else if (order === "sort-desc")
      rowsdata.sort((a, b) => {
        const aval = fn ? fn(a) : a[columnClicked.headerName];
        const bval = fn ? fn(b) : b[columnClicked.headerName];
        return bval > aval ? 1 : aval === bval ? 0 : -1;
      });
    setRowsToDisplay([...rowsdata]);
  };

  // options controls
  const handleTableOptionsClick = (e) => {
    const _field = e.target.dataset.optionstype;
    console.log(columnClicked, _field, rowsToDisplay);
    setOptionsVisible(false);
    const rowsdata = [...rowsToDisplay];

    let _columns = [...columns];
    if (
      _field === "del" &&
      !reduxSubscription?.plan?.name?.includes("Standard")
    ) {
      _columns = _columns.filter(
        (col) => col.name !== columnClicked.headerName,
      );
      updateColumns(_columns, "delete");
    }
    if (columnTypeMapping[columnClicked.field] === "object") {
      sortRows(
        rowsdata,
        _field,
        (val) => val[columnClicked.field][sortableKeys[columnClicked.field]],
      );
    } else if (columnClicked.type === "dateTime") {
      sortRows(
        rowsdata,
        _field,
        (val) => new Date(val[columnClicked.headerName]),
      );
    } else if (columnTypeMapping[columnClicked.field] === "string") {
      sortRows(rowsdata, _field);
    } else {
      sortRows(rowsdata, _field);
    }
  };

  // click handler for Add Column button
  const handleAddColumn = (e) => {
    if (!reduxSubscription?.plan?.name?.includes("Standard")) {
      setAddColumn((prevState) => {
        return {
          ...prevState,

          name: "",
          data: "",
          call_type: "",
          call_status: "",
          duration_filter: { filter: "none", start: "", end: "" },
        };
      });
      setAddColumnModalVisible(true);
      document.body.style.overflowY = "hidden";
    } else
      enqueueSnackbar(
        "Customizable reports are not included in Standard plan",
        { variant: "error" },
      );
  };

  const handleOverlayClick = (e) => {
    // setAddColumn(prevState => ({...prevState, data:{colName:'', colData:'', colCallType: '', colCallStatus:'', colDurationFilter:{filter:'none',start:'', end:''},}, isVisible: false, add:false}))
    setAddColumn({
      name: "",
      data: "",
      call_type: "",
      call_status: "",
      duration_filter: { filter: "none", start: "", end: "" },
    });
    setAddColumnModalVisible(false);
    setErrorBorder({ colNameError: false });
    document.body.style.overflowY = "auto";
  };

  const handleAddColumnCancelClick = (e) => {
    setAddColumn({
      name: "",
      data: "",
      call_type: "",
      call_status: "",
      duration_filter: { filter: "none", start: "", end: "" },
    });
    setAddColumnModalVisible(false);
    setErrorBorder({ colNameError: false });
    document.body.style.overflowY = "auto";
  };
  // click on close icon of add column
  const handleAddColumnCloseClick = (e) => {
    setAddColumn({
      name: "",
      data: "",
      call_type: "",
      call_status: "",
      duration_filter: { filter: "none", start: "", end: "" },
    });
    setAddColumnModalVisible(false);
    setErrorBorder({ colNameError: false });
    document.body.style.overflowY = "auto";
  };

  const handleTableDurationFilterChange = (e) => {
    const __filter = e.target.value;

    setAddColumn((prevState) => {
      return {
        ...prevState,
        duration_filter: {
          filter: __filter,
          start: __filter === "none" ? "" : "0",
          end: __filter === "none" ? "" : __filter === "between" ? "0" : "",
        },
      };
    });
  };

  const handleAddColumnDataChange = (e) => {
    const __field = e.target.dataset.coldatafield;
    setAddColumn((prevState) => {
      if (__field === "duration_filter") {
        const _prevFilter = prevState.duration_filter.filter;
        if (_prevFilter === "less than" || _prevFilter === "greater than")
          return {
            ...prevState,

            duration_filter: {
              ...prevState.duration_filter,
              start: String(e.target.value),
            },
          };
        else if (_prevFilter === "between") {
          if (e.target.dataset.coldurationminfield === "start")
            return {
              ...prevState,

              duration_filter: {
                ...prevState.duration_filter,
                start: String(e.target.value),
              },
            };
          if (e.target.dataset.coldurationminfield === "end")
            return {
              ...prevState,

              duration_filter: {
                ...prevState.duration_filter,
                end: String(e.target.value),
              },
            };
        }
      } else if (__field === "call_status")
        return {
          ...prevState,
          call_status: e.target.value,
        };
      else if (__field === "call_type")
        return {
          ...prevState,
          call_type: e.target.value,
        };
      else if (__field === "data")
        return {
          ...prevState,
          data: e.target.value,
        };
      else if (__field === "contact_status")
        return {
          ...prevState,
          contact_status: e.target.value,
        };
      else if (__field === "name") {
        const _value = e.target.value;
        setErrorBorder((prevState) => ({
          ...prevState,
          colNameError: !columns.find((tc) => tc?.name === _value)
            ? false
            : true,
        }));
        return {
          ...prevState,
          name: _value,
        };
      }
    });
  };

  //checking validity of metadata of new custom column and req to save at backend.
  const handleAddColumnSave = (e) => {
    e.preventDefault();
    if (addColumn?.name?.trim().length === 0 || addColumn?.data?.length === 0) {
      const message =
        addColumn?.name?.trim().length === 0
          ? "Please give a valid name for the column."
          : 'Please select the type of data you want in the column under "Column Data".';
      enqueueSnackbar(message, { variant: "error" });
      return;
    }
    //getCustomColumnData();
    if (gtableCols.map((col) => col.headerName).includes(addColumn?.name)) {
      setErrorBorder((prevState) => ({ ...prevState, colNameError: true }));
      enqueueSnackbar(
        "Column name has already been used. Please choose another name",
        { variant: "error" },
      );
      return;
    }
    setErrorBorder((prevState) => ({ ...prevState, colNameError: false }));
    const updatedColumns = [...columns, addColumn];

    updateColumns(updatedColumns, "add");
    handleAddColumnCloseClick("a");
  };

  const updateColumns = (data, action) => {
    JService.patch("/api/v1/auth/update-userprofile/", {
      analytics_metrics: data,
    })
      .then((res) => {
        if (!res?.success) {
          throw new Error(res?.message || "Could not update columns.");
        }

        const updatedColumns = res?.data?.analytics_metrics;
        const hasValidColumns =
          Array.isArray(updatedColumns) && updatedColumns.length > 0;

        setColumns(hasValidColumns ? updatedColumns : defaultColData);
        fetchCallAnalytics(calendar, null, true);
        enqueueSnackbar(
          `Column has been successfully ${action === "add" ? "added" : action === "delete" ? "deleted" : "updated"}.`,
          {
            variant: "success",
          },
        );
      })
      .catch((error) => {
        console.error("Failed to update columns:", error);
        enqueueSnackbar(error?.message || "Unexpected error occurred.", {
          variant: "error",
        });
      });
  };

  const convertToMinutesAndSeconds = (totalMinutes) => {
    const totalSeconds = Math.floor(totalMinutes * 60);

    if (totalSeconds === 0) {
      return 0;
    }

    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    if (seconds === 60) {
      return `${minutes + 1}m 0s`;
    } else if (minutes === 60) {
      return `${Math.floor(minutes / 60)}h 0m`;
    }

    const formattedMinutes = minutes > 0 ? `${minutes}m` : "";
    const formattedSeconds = seconds > 0 ? `${seconds}s` : "";

    return `${formattedMinutes} ${formattedSeconds}`.trim();
  };

  //creating column metadata for gtable
  const createGtableColMetadata = (columns) => {
    if (!columns || !Array.isArray(columns) || columns.length <= 0) return;
    const columnsWithUsers = [
      { name: "User", minWidth: 256, field: "analyticsUser" },
      ...columns,
    ];
    const gtableColMetadata = columnsWithUsers.map((col) => {
      const colname = col["name"];
      let temp = "";
      return {
        field: col["field"] || col["headerName"] || colname,
        headerName: colname,
        type: col["data"] === "last activity" ? "dateTime" : "string",
        cellClassName: "gtable-cell",
        headerClassName: "dashboard-header",
        minWidth: col["minWidth"] ?? 220,
        flex: 1,
        colData: col["data"],
        renderHeader: () => {
          const params = {
            field: capitalizeString(
              col["field"] || col["headerName"] || colname,
            ),
            headerName: colname,
            type: col["data"] === "last activity" ? "dateTime" : "string",
          };
          return (
            <div className="dashboard--column-header">
              <span className="spanNoOverflow">{colname}</span>
              <span
                onClick={(e) => onColumnHeaderClick(params, e)}
                className={`dashboard--column-header-icon${optionsVisible && columnClicked?.headerName === params.headerName ? "--visible" : ""}`}
              >
                <MoreVert />
              </span>
            </div>
          );
        },
        renderCell: (params) => {
          if (
            col["data"] === "total minutes" ||
            col["data"] === "average minutes"
          ) {
            temp =
              getFormattedTime(params.row[colname]) === 0
                ? "0"
                : getFormattedTime(params.row[colname]);
          } else if (col["data"] === "status") {
            return (
              <div className="status-container">
                <span className={`status-circle ${params?.row?.Status}`}></span>
                <p className="t7 regular-font">{params?.row?.Status}</p>
              </div>
            );
          } else if (col["data"] === "last activity") {
            if (
              params?.row?.[colname] &&
              params?.row?.[colname] != "--" &&
              params?.row?.Status?.toLowerCase() !== "on a call"
            )
              return (
                <div style={{ paddingLeft: "14px" }}>
                  <p className="t7 regular-font">
                    {calcDate(params.row[colname])}
                  </p>
                  <p
                    className="t7 regular-font nc-gray-600"
                    style={{ marginTop: 0 }}
                  >
                    {formatTime(params.row[colname])}
                  </p>
                </div>
              );
            else return <p style={{ paddingLeft: "14px" }}>--</p>;
          } else if (colname === "User") {
            return (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "flex-start",
                  paddingLeft: "14px",
                }}
              >
                <span className="t7 regular-font nc-gray-900">
                  {params?.row?.User?.name}
                </span>
                <span className="t7 regular-font nc-gray-600">
                  {params?.row?.User?.email}
                </span>
              </div>
            );
          }
          return (
            <p className="regular-font b1" style={{ marginLeft: "14px" }}>
              {temp || params.row[colname]}
            </p>
          );
        },
        sortable: false,
      };
    });

    setGtableCols(gtableColMetadata);
  };

  const prevReduxOrg = useRef(reduxOrg?.selectedOrg);

  useEffect(() => {
    if (
      prevReduxOrg.current !== reduxOrg?.selectedOrg &&
      (Boolean(reduxOrg?.selectedOrg) || reduxOrg?.selectedOrg === 0)
    ) {
      // reduxOrg changed
      // setorgdetails again if allOrgs?.length > 1 && primaryAccount holder
      setShowLoader(false);
      prevReduxOrg.current = reduxOrg?.selectedOrg;
      setController((prevState) => ++prevState);
    }
  }, [reduxOrg]);

  const defaultColData = [
    {
      name: "Total calls",
      data: "total calls",
      call_type: "incoming and outgoing",
      call_status: "all",
      duration_filter: {
        filter: "none",
        start: "",
        end: "",
      },
    },
    {
      name: "Total duration",
      data: "total minutes",
      call_type: "incoming and outgoing",
      call_status: "all",
      duration_filter: {
        filter: "none",
        start: "",
        end: "",
      },
    },
  ];

  function handleOptionsCloseOnClickAway(e) {
    const path = e.composedPath()?.map((pathItem) => {
      let newPathItem = null;
      newPathItem =
        pathItem?.tagName?.toLowerCase() +
        (pathItem?.className ? "." + pathItem?.className : "");
      return newPathItem;
    });
    // clicked outside
    if (
      !path?.length ||
      (!path?.includes("div.table-dropdown-container") &&
        !path.includes("span.dashboard--column-header-icon"))
    ) {
      setOptionsVisible(false);
      window.removeEventListener("click", handleOptionsCloseOnClickAway);
    }
  }

  useEffect(() => {
    if (optionsVisible) {
      window.addEventListener("click", handleOptionsCloseOnClickAway);
    }
    return () => {
      window.removeEventListener("click", handleOptionsCloseOnClickAway);
    };
  }, [optionsVisible]);

  const onColumnHeaderClick = (params, event) => {
    setColumnClicked({
      field: params.field,
      headerName: params.headerName,
      type: params.type,
    });
    setOptionsPosition(`translate(${event.pageX - 448}px, 70.5px)`);
    setOptionsVisible((prev) => !prev);
  };

  const handleFilterApply = (filterName, val) => {
    if (filterName === "Date") {
      setCalendar(val);
    }
  };
  return (
    <div className="overview-container">
      <Filters
        onApply={handleFilterApply}
        availableData={availableData}
        dispatchAvailableData={dispatchAvailableData}
        fromScreen="dashboard"
        defaultDateRange={0}
        filtersList={[
          {
            name: "Users",
            isSearch: true,
            payloadFor: "users",
            permission: "view users",
          },
          {
            name: "Team",
            isSearch: true,
            payloadFor: "teams",
            permission: "view teams",
          },
          {
            name: "List",
            isSearch: true,
            payloadFor: "lists",
            permission: "view contacts and lists",
          },
        ]}
      />

      {showLoader ? (
        <AnimationPage />
      ) : (
        resizeController > -1 && (
          <>
            <div className="dashboard-charts-container">
              <div className="dashboard-container">
                <div className="dashboard-stats-container">
                  <div className="dashboard-overview-stats regular-font">
                    <p className="title t6">Users</p>
                    <p className="t4 medium-font">
                      {analyticsData?.total_users || 0}
                    </p>
                  </div>
                  <div className="dashboard-overview-stats regular-font">
                    <p className="title t6">Total calls</p>
                    <p className="t4 medium-font">
                      {analyticsData?.total_calls || 0}
                    </p>
                  </div>
                  <div className="dashboard-overview-stats regular-font">
                    <p className="title t6">Unique calls</p>
                    <p className="t4 medium-font">
                      {analyticsData?.unique_calls || 0}
                    </p>
                  </div>
                  <div className="dashboard-overview-stats regular-font">
                    <p className="title t6">Call duration</p>
                    <p className="t4 medium-font">
                      {analyticsData?.total_duration
                        ? convertToMinutesAndSeconds(
                            analyticsData?.total_duration / 60000,
                          )
                        : 0}
                    </p>
                  </div>
                </div>
                <div className="dashboard-linechart-container">
                  {/* <div>CALL VOLUME</div> */}
                  <div style={{ height: "calc(100%)", width: "100%" }}>
                    <Line options={options} data={chartData} />
                  </div>
                  <div className="legend-container">
                    <div className="legend">
                      <div className="legend-box-1"></div>
                      <p className="t7 regular-font">Total calls</p>
                    </div>
                    <div className="legend">
                      <div className="legend-box-2"></div>
                      <p className="t7 regular-font">Answered calls</p>
                    </div>
                    <div className="legend">
                      <div className="legend-box-3"></div>
                      <p className="t7 regular-font">Unanswered calls</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="dashboard-chart-container">
                {!showLoader && (
                  <div
                    style={{
                      height: "max-content",
                      maxWidth: `calc(${document.body.clientWidth}px - var(--side-nav-width) - 64px)`,
                    }}
                  >
                    {
                      <GTable
                        className="dashboard-gtable"
                        leftHeader={{
                          filters: [],
                          isSearch: true,
                        }}
                        searchFields={["User"]}
                        rightHeader={{
                          isDownload: true,
                          isAddColumn: true,
                        }}
                        addColumnHandler={handleAddColumn}
                        addColumnHandlerDisabled={reduxSubscription?.plan?.name?.includes(
                          "Standard",
                        )}
                        rows={rowsToDisplay}
                        availableData={availableData}
                        hideHeader={false}
                        dispatchAvailableData={dispatchAvailableData}
                        download={{
                          data: csvData,
                          body: null,
                          filename:
                            "users-table-" +
                            new Date().toLocaleString("en-US", {
                              weekday: "long",
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            }),
                        }}
                        onClickDownloadIcon={clickCSVDownloadIcon}
                        fromScreen="analytics-dashboard"
                        columns={gtableCols}
                        useMuiTable={true}
                        isLoading={showLoader}
                        onRowClick={() => null}
                      />
                    }
                  </div>
                )}

                {/* table dropdown */}
                <div
                  style={{
                    display: optionsVisible ? "block" : "none",
                    transform: optionsPosition,
                  }}
                  className="table-dropdown-container"
                >
                  <div
                    className="table-dropdown-item"
                    data-optionstype="sort-asc"
                    onClick={(e) => handleTableOptionsClick(e)}
                  >
                    <img
                      className="table-dropdown-item-icon"
                      data-optionstype="sort-asc"
                      src={tableArrowAscending}
                      alt="Sort ascending"
                    />
                    <span
                      className="nc-gray-900 regular-font t7"
                      data-optionstype="sort-asc"
                    >
                      Sort ascending
                    </span>
                  </div>
                  <div
                    className="table-dropdown-item"
                    data-optionstype="sort-desc"
                    onClick={(e) => handleTableOptionsClick(e)}
                  >
                    <img
                      className="table-dropdown-item-icon"
                      data-optionstype="sort-desc"
                      src={tableArrowDescending}
                      alt="Sort descending"
                    />
                    <span
                      className="nc-gray-900 regular-font"
                      data-optionstype="sort-desc"
                    >
                      Sort descending
                    </span>
                  </div>

                  <div className="table-dropdown-sep"></div>

                  <button
                    className="table-dropdown-item table-dropdown-item-del"
                    disabled={
                      reduxSubscription?.plan?.name?.includes("Standard") ||
                      //tableSearch?.isSearch
                      (availableData.searchText &&
                      availableData.searchText.trim()?.length > 0
                        ? true
                        : !["User"].includes(columnClicked.field)
                          ? columns.length > 1
                            ? false
                            : true
                          : true)
                    }
                    data-optionstype="del"
                    onClick={(e) => handleTableOptionsClick(e)}
                  >
                    <img
                      className="table-dropdown-item-icon"
                      data-optionstype="del"
                      src={tableDeleteColumn}
                      alt="Delete column"
                    />
                    <span
                      className="nc-gray-900 regular-font"
                      data-optionstype="del"
                    >
                      Delete Column
                    </span>
                  </button>
                </div>
              </div>
            </div>
            {/* NOTE: ADD COLUMN */}
            <div
              className="table-add-column-container"
              style={{ display: addColumnModalVisible ? "block" : "none" }}
            >
              <div className="header">
                <span className="medium-font t6 nc-gray-900">
                  {/* {addColumn?.add ? "Add Column" : "Edit Column"} */}
                  Add Column
                </span>
                <img
                  src={addColumnClose}
                  onClick={(e) => handleAddColumnCloseClick(e)}
                />
              </div>
              <div className="add-column-body nc-gray-900">
                <div className="item nc-gray-900">
                  <label>
                    <span className="medium-font t7">Column Name*</span>
                    <input
                      type="text"
                      className="regular-font t6 nc-gray-900"
                      style={{
                        border: errorBorder?.colNameError
                          ? "1px solid red"
                          : "1px solid #ddd",
                      }}
                      value={addColumn?.name}
                      onChange={(e) => handleAddColumnDataChange(e)}
                      data-coldatafield="name"
                    />
                  </label>
                </div>
                <div className="item nc-gray-900">
                  <label className="medium-font">
                    <span className="medium-font t7">Column Data*</span>
                    <select
                      onChange={(e) => handleAddColumnDataChange(e)}
                      data-coldatafield="data"
                      className="regular-font t6 nc-gray-900"
                      value={addColumn?.data}
                    >
                      <option value="">Select column data</option>
                      <option
                        value="total minutes"
                        disabled={addColumn?.call_status === "unique"}
                      >
                        Total duration
                      </option>

                      <option
                        value="average minutes"
                        disabled={addColumn?.call_status === "unique"}
                      >
                        Average duration
                      </option>
                      <option value="total calls">Number of calls</option>
                      {reduxOrg?.data?.show_assigned_contacts_filter && (
                        <option value="assigned contacts">
                          Assigned contacts
                        </option>
                      )}
                      {isPro && (
                        <option value="last activity">Last activity</option>
                      )}
                      {isPro && <option value="status">Live status</option>}
                    </select>
                  </label>
                </div>
                {[
                  "total calls",
                  "average minutes",
                  "total minutes",
                  "assigned contacts",
                ].includes(addColumn?.data) && (
                  <div className="add-column-sep"></div>
                )}
                {addColumn?.data === "assigned contacts" && (
                  <div className="item nc-gray-900">
                    <label
                      style={{
                        flexDirection: "column",
                        alignItems: "flex-start",
                      }}
                    >
                      <span className="medium-font t7">Contact status</span>
                      <select
                        onChange={handleAddColumnDataChange}
                        data-coldatafield="contact_status"
                        className="regular-font t6 nc-gray-900"
                        value={addColumn?.contact_status}
                      >
                        <option value="both">Reached + Not reached</option>
                        <option value="reached">Reached</option>
                        <option value="not reached">Not Reached</option>
                      </select>
                    </label>
                  </div>
                )}

                {["total minutes", "average minutes", "total calls"].includes(
                  addColumn?.data,
                ) && (
                  <>
                    <div className="item nc-gray-900">
                      <label>
                        <span className="medium-font t7">Call Type</span>
                        {/*  <input type="text" /> */}
                        <select
                          onChange={(e) => handleAddColumnDataChange(e)}
                          data-coldatafield="call_type"
                          className="regular-font t6 nc-gray-900"
                          value={addColumn?.call_type}
                        >
                          <option value="incoming and outgoing">
                            Incoming + Outgoing calls
                          </option>
                          <option value="incoming">Incoming Calls</option>
                          <option value="outgoing">Outgoing Calls</option>
                        </select>
                      </label>
                    </div>
                    <div className="item nc-gray-900">
                      <label>
                        <span className="medium-font t7 nc-gray-900">
                          Call Status
                        </span>
                        <select
                          onChange={(e) => handleAddColumnDataChange(e)}
                          data-coldatafield="call_status"
                          className="regular-font t6 nc-gray-900a"
                          value={addColumn?.call_status}
                        >
                          <option value="all">All Calls</option>
                          <option
                            value="unique"
                            disabled={
                              addColumn?.data === "total minutes" ||
                              addColumn?.data === "average minutes"
                            }
                          >
                            Unique
                          </option>
                          <option value="answered">Answered</option>
                          <option value="unanswered">Unanswered</option>
                        </select>
                      </label>
                    </div>
                    <div className="item nc-gray-900">
                      <span
                        style={{ marginBottom: "4px" }}
                        className="medium-font t7"
                      >
                        Duration Filter
                      </span>
                      <label className="duration-filter">
                        <select
                          onChange={(e) => handleTableDurationFilterChange(e)}
                          className="regular-font t6 nc-gray-900"
                          value={addColumn?.duration_filter?.filter}
                        >
                          <option value="none">None</option>
                          <option value="less than">Less than</option>
                          <option value="greater than">Greater than</option>
                          <option value="between">Between</option>
                        </select>
                        {addColumn?.duration_filter?.filter !== "none" &&
                          addColumn?.duration_filter?.filter !== null && (
                            <div className="duration-time-fields">
                              {addColumn?.duration_filter?.filter !== "none" &&
                                addColumn?.duration_filter?.filter !== null && (
                                  <input
                                    type="number"
                                    className="regular-font t6 nc-gray-900"
                                    placeholder="Mins"
                                    onChange={(e) =>
                                      handleAddColumnDataChange(e)
                                    }
                                    data-coldatafield="duration_filter"
                                    data-coldurationminfield="start"
                                    value={addColumn?.duration_filter?.start}
                                    min={0}
                                    max={
                                      addColumn?.duration_filter?.end !== "0" &&
                                      addColumn?.duration_filter?.end !== ""
                                        ? addColumn?.duration_filter?.end
                                        : 10000
                                    }
                                  />
                                )}
                              {addColumn?.duration_filter?.filter ===
                                "between" && (
                                <span className="regular-font t6">to</span>
                              )}
                              {addColumn?.duration_filter?.filter ===
                                "between" && (
                                <input
                                  type="number"
                                  className="regular-font t6 nc-gray-900 ui--input ui--between"
                                  placeholder="Mins"
                                  onChange={(e) => handleAddColumnDataChange(e)}
                                  data-coldatafield="duration_filter"
                                  data-coldurationminfield="end"
                                  value={addColumn?.duration_filter?.end}
                                  min={addColumn?.duration_filter?.start}
                                />
                              )}
                            </div>
                          )}
                      </label>
                    </div>
                  </>
                )}
              </div>
              <div className="add-column-button-container">
                <input
                  onClick={handleAddColumnCancelClick}
                  type="button"
                  value="Cancel"
                  className="btn btn-gray medium-font t7 nc-gray-900 ui--button ui--cancel"
                />
                <Button
                  className="ui--button ui--save"
                  disabled={false}
                  hierarchy="green"
                  variant="contained"
                  buttonText="Add"
                  onClick={handleAddColumnSave}
                  styleOverride={{ marginRight: "32px" }}
                />
              </div>
            </div>
          </>
        )
      )}
      <div
        className="add-new-campaign-overlay-temp table-add-column-overlay"
        onClick={(e) => handleOverlayClick(e)}
        style={{
          display: addColumnModalVisible ? "block" : "none",
          width: "200vw",
          left: "-240px",
        }}
      ></div>
    </div>
  );
}
